import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiErr, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FileUpload from "@/components/marketplace/FileUpload";
import { trackEvent } from "@/lib/analytics";
import Seo, { SITE_URL, BREADCRUMB_JSONLD } from "@/components/Seo";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Clock, Layers } from "lucide-react";
import { responsiveImage } from "@/lib/img";

// Category labels are authored with their unit already in them ("Stone
// dimensions (mm)"), so appending f.unit blindly produced "… (mm) (mm)".
function labelWithUnit(f) {
  if (!f.unit) return f.label;
  const label = f.label || "";
  return new RegExp(`\\(\\s*${f.unit.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\)`, "i").test(label)
    ? label
    : `${label} (${f.unit})`;
}

function DynField({ f, value, onChange, id, labelId, invalid }) {
  const testid = `field-${f.key}`;
  // Native controls get the id so the <label for> points at them; grouped
  // controls have no single focusable element, so they borrow the label by id.
  const a11y = { "aria-invalid": invalid || undefined };
  const fieldProps = { ...a11y, id };
  const groupProps = { ...a11y, role: undefined, "aria-labelledby": labelId };
  const ring = invalid ? "border-destructive" : "hairline";

  if (f.type === "textarea") return <Textarea {...fieldProps} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`bg-transparent border ${ring} rounded-none min-h-[100px]`} />;
  if (f.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger {...fieldProps} data-testid={testid} className={`bg-transparent border ${ring} rounded-none`}><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>
          {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "radio") {
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid} {...groupProps} role="radiogroup" id={id} tabIndex={-1}>
        {(f.options || []).map((o) => (
          <button key={o} type="button" role="radio" aria-checked={value === o} onClick={() => onChange(o)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors duration-300 ${value === o ? "border-primary bg-primary text-primary-foreground" : `${ring} hover:border-primary/60`}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid} {...groupProps} role="group" id={id} tabIndex={-1}>
        {(f.options || []).map((o) => (
          <button key={o} type="button" aria-pressed={arr.includes(o)} onClick={() => toggle(o)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors duration-300 ${arr.includes(o) ? "border-primary bg-primary text-primary-foreground" : `${ring} hover:border-primary/60`}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "checkbox") {
    return (
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Checkbox {...fieldProps} data-testid={testid} checked={!!value} onCheckedChange={onChange} className="rounded-none border-primary/50" />
        <span>Yes</span>
      </div>
    );
  }
  if (f.type === "number" || f.type === "measurement") return <Input {...fieldProps} data-testid={testid} type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`bg-transparent border ${ring} rounded-none`} placeholder={f.unit ? `in ${f.unit}` : undefined} />;
  if (f.type === "date") return <Input {...fieldProps} data-testid={testid} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={`bg-transparent border ${ring} rounded-none`} />;
  return <Input {...fieldProps} data-testid={testid} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={`bg-transparent border ${ring} rounded-none`} />;
}

export default function CategoryDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [cat, setCat] = useState(null);
  const [values, setValues] = useState({});
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [accept, setAccept] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  // Clearing as soon as the field is corrected keeps the error from nagging.
  const clearError = (key) => setErrors((s) => (s[key] ? { ...s, [key]: undefined } : s));

  useEffect(() => { api.get(`/categories/${slug}`).then((r) => setCat(r.data)).catch(() => nav("/categories")); }, [slug, nav]);

  // A transient toast made the user hunt for the offending field, so errors are
  // rendered next to their input and the first one takes focus.
  const focusFirstError = (key) => {
    const el = document.getElementById(`qf-${key}`);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.focus({ preventScroll: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) { nav(`/login?next=/categories/${slug}`); return; }
    if (user.role !== "seller") { toast.error("Only client accounts can request quotations."); return; }
    const next = {};
    (cat.custom_fields || []).forEach((f) => {
      const v = values[f.key];
      const empty =
        v == null ||
        (typeof v === "string" && !v.trim()) ||
        (Array.isArray(v) && v.length === 0) ||
        (f.type === "checkbox" && v === false);
      if (f.required && empty) next[f.key] = `${labelWithUnit(f)} is required.`;
    });
    if (!description || description.trim().length < 20) {
      next.description = "Please describe your piece in at least 20 characters.";
    }
    if (!accept) next.accept = "Please accept the revision and scope policy.";
    setErrors(next);
    const firstKey = Object.keys(next)[0];
    if (firstKey) {
      toast.error(Object.keys(next).length === 1 ? next[firstKey] : "Please fix the highlighted fields.");
      focusFirstError(firstKey);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", {
        category_id: cat.id,
        description,
        field_responses: values,
        attachments: attachments.map((f) => f.id),
        accept_revision_policy: true,
        currency,
      });
      trackEvent({ category: "quote", action: "quote_submitted", label: cat.slug });
      toast.success(`Quote ${data.ref} submitted.`);
      nav(`/seller/quotes/${data.id}`);
    } catch (ex) { toast.error(apiErr(ex)); } finally { setBusy(false); }
  };

  if (!cat) return <div className="p-20 text-center text-muted-foreground">Loading…</div>;

  const catDesc = (cat.short_desc || cat.long_desc || `Bespoke ${cat.name} jewellery CAD, designed by GetJewelDesigns' Design Team.`).slice(0, 300);
  // The category's own name ("Ring") says what the piece is but not what this
  // page sells, so on its own it made a <title> and an <h1> that matched no
  // search anyone performs. Naming the service alongside it — "Ring CAD design"
  // — is what people actually type, and it reads the same way for every
  // category without per-category copy having to exist in the database.
  const catTitle = `${cat.name} CAD design`;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: catTitle,
    description: catDesc,
    image: cat.cover_image,
    brand: { "@type": "Brand", name: "GetJewelDesigns" },
    category: "Jewellery CAD design",
    offers: {
      "@type": "Offer",
      price: cat.starting_price,
      priceCurrency: cat.currency || "INR",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/categories/${slug}`,
    },
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-16">
      <Seo
        title={catTitle}
        description={catDesc}
        path={`/categories/${slug}`}
        image={cat.cover_image}
        type="product"
        jsonLd={[
          productJsonLd,
          BREADCRUMB_JSONLD([
            { name: "Categories", path: "/categories" },
            { name: cat.name, path: `/categories/${slug}` },
          ]),
        ]}
      />
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-5 min-w-0">
          <div className="aspect-[4/5] overflow-hidden border hairline mb-6">
            <img {...responsiveImage(cat.cover_image, { width: 800, ratio: "4/5", sizes: "(min-width: 768px) 40vw, 100vw" })} alt={cat.name} className="w-full h-full object-cover" />
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Category</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-6">{catTitle}</h1>
          <p className="text-muted-foreground leading-relaxed mb-8">{cat.long_desc || cat.short_desc}</p>
          <div className="space-y-4 border hairline p-5 md:p-6">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Starting from</span>
              <span className="font-serif text-2xl md:text-3xl text-primary whitespace-nowrap">
                {currency === "USD" && cat.starting_price_usd
                  ? fmtMoney(cat.starting_price_usd, "USD")
                  : fmtMoney(cat.starting_price, cat.currency)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground border-t hairline pt-4 gap-3">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0" strokeWidth={1}/>Turnaround</span>
              <span className="whitespace-nowrap">{cat.turnaround_days} day{cat.turnaround_days > 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground gap-3">
              <span className="flex items-center gap-2"><Layers className="h-4 w-4 shrink-0" strokeWidth={1}/>Included revisions</span>
              <span className="whitespace-nowrap">{cat.included_revisions}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground gap-3">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 shrink-0" strokeWidth={1}/>Verified specialists</span>
              <span className="whitespace-nowrap">Design Team</span>
            </div>
          </div>
        </div>

        {/* noValidate: the browser's native bubble fires before onSubmit and only
            ever flags the first `required` control, which pre-empted the inline
            messages below. Validation is owned by submit() so every offending
            field is marked at once. */}
        <form onSubmit={submit} noValidate className="col-span-12 md:col-span-7 space-y-8 min-w-0" data-testid="quote-form">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Request a quotation</div>
            <h2 className="font-serif text-3xl md:text-4xl">Tell us about your piece.</h2>
            <p className="text-muted-foreground mt-3">Complete these details and share references. Our team will review and return with a curated quotation.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cat.custom_fields?.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <Label id={`qf-${f.key}-label`} htmlFor={`qf-${f.key}`} className="text-xs uppercase tracking-widest">
                  {labelWithUnit(f)}{f.required && <span className="text-primary" aria-hidden="true">*</span>}
                  {f.required && <span className="sr-only"> (required)</span>}
                </Label>
                <div className="mt-2">
                  <DynField
                    f={f}
                    id={`qf-${f.key}`}
                    labelId={`qf-${f.key}-label`}
                    invalid={!!errors[f.key]}
                    value={values[f.key]}
                    onChange={(v) => { setValues((s) => ({ ...s, [f.key]: v })); clearError(f.key); }}
                  />
                </div>
                {errors[f.key] && <p role="alert" className="text-xs text-destructive mt-2">{errors[f.key]}</p>}
              </div>
            ))}
          </div>

          <div>
            <Label htmlFor="qf-currency" className="text-xs uppercase tracking-widest">Billing currency</Label>
            <div className="mt-2">
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="qf-currency" data-testid="quote-currency" className="bg-transparent border hairline rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">₹ Indian Rupee (INR)</SelectItem>
                  <SelectItem value="USD">$ US Dollar (USD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Your quotation and payment will be in this currency.</p>
          </div>

          <div>
            <Label htmlFor="qf-description" className="text-xs uppercase tracking-widest">
              Detailed description<span className="text-primary" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
            </Label>
            <Textarea
              id="qf-description"
              data-testid="quote-description"
              value={description}
              onChange={(e) => { setDescription(e.target.value); clearError("description"); }}
              required
              minLength={20}
              aria-invalid={!!errors.description || undefined}
              className={`mt-2 bg-transparent border ${errors.description ? "border-destructive" : "hairline"} rounded-none min-h-[140px]`}
              placeholder="Describe your vision, references, occasion, budget indicators…"
            />
            {errors.description && <p role="alert" className="text-xs text-destructive mt-2">{errors.description}</p>}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Reference files</Label>
            <div className="mt-2">
              <FileUpload value={attachments} onChange={setAttachments} category="quote_reference" testid="quote-refs" />
            </div>
          </div>

          <div className="border hairline p-5 text-sm text-muted-foreground">
            Your quotation includes up to {cat.included_revisions} revisions unless otherwise specified. Revisions cover reasonable changes to the approved design requirements. New concepts, major scope changes or additional deliverables may require a separate quotation.
          </div>

          <div>
            <label className="flex items-start gap-3 text-sm text-muted-foreground">
              <Checkbox id="qf-accept" data-testid="quote-accept" checked={accept} aria-invalid={!!errors.accept || undefined} onCheckedChange={(v) => { setAccept(v); clearError("accept"); }} className={`rounded-none mt-1 ${errors.accept ? "border-destructive" : "border-primary/50"}`} />
              <span>I accept the revision and scope policy.</span>
            </label>
            {errors.accept && <p role="alert" className="text-xs text-destructive mt-2">{errors.accept}</p>}
          </div>

          <Button type="submit" data-testid="quote-submit" disabled={busy} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs py-6 px-10">
            {busy ? "Sending…" : (<span className="inline-flex items-center gap-3">Request quotation <ArrowRight className="h-4 w-4" strokeWidth={1} /></span>)}
          </Button>
          {(!user || user === false) && (
            <div className="text-xs text-muted-foreground">You'll be asked to <Link className="text-primary underline underline-offset-4" to={`/login?next=/categories/${slug}`}>sign in</Link> to submit.</div>
          )}
        </form>
      </div>
    </div>
  );
}
